import { STATIC_PATH_FALLBACK } from 'constants/environment';
import GuideStructuredData from 'components/Pages/GuideStructuredData';
import NotFound from 'components/Pages/NotFound';
import AuthorByLine from 'components/Shared/AuthorByLine';
import ConversionBar from 'components/Shared/ConversionBar';
import Breadcrumbs from 'components/ui/Breadcrumbs';
import BlockLoaderNonFields from 'components/ui/ContentBlocks/BlockLoaderNonFields';
import Layout from 'components/ui/Layout';
import config from 'config';
import GET_GUIDE_PAGE from 'data/graphql/queries/guidePage';
import { getMainFooter, skipServerSideBuild } from 'data/page/utils';
import { getBrand } from 'data/page/utils/getBrand';
import { getNormalizedAuthorGql } from 'data/utils/author';
import {
  contentfulClientPreview,
  fetchContentfulGql,
  getAlternatePages,
} from 'services/contentful';
import { getContentfulLocale } from 'utils/locale';
import Head from 'next/head';
import TableOfContents, { getTitles } from 'components/Shared/TableOfContents';
import styles from 'components/Pages/Guide.module.scss';
import type { GetStaticPaths, GetStaticProps } from 'next';
import type { ParsedUrlQuery } from 'querystring';
import type { AlternateSlug } from 'types/AlternateSlugs';
import type { PageAuthors } from 'types/Author';
import type { Brand } from 'types/Brand';
import type { IGuidePageFields } from 'types/contentful/generated';
import type { FooterItemType } from 'types/Footer';
import type { GuideItem } from 'types/Guide';
import type { ConfigLocale, PageData } from 'types/PageData';

interface GuideProps {
  entry: GuideItem;
  preview: boolean;
  alternateSlugs: AlternateSlug[];
  footer: FooterItemType | null;
  pageAuthors: PageAuthors;
  brand: Brand | null;
}

const Guide = ({
  entry,
  preview,
  alternateSlugs,
  footer,
  pageAuthors,
  brand,
}: GuideProps): JSX.Element => {
  if (!entry) {
    return <NotFound brand={brand} footer={footer} />;
  }

  const titles = getTitles(entry.blocks);
  const isTableOfContentsShown = titles && entry.isTableOfContentsEnabled;

  const breadcrumbs = [
    {
      link: entry.slug,
      title: entry.title,
    },
  ];

  return (
    <Layout
      preview={preview}
      searchIndex={!entry.disableIndexing && !entry.disableLocale}
      title={entry.metaTitle || ''}
      metaDescription={entry.metaDescription}
      alternateSlugs={alternateSlugs}
      alternateSlugsPrefix="guides"
      footer={footer}
      noMetaTitle={entry.title || ''}
      brand={brand}
    >
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({ dateModified: entry?.sys?.publishedAt }),
          }}
        />
      </Head>
      <GuideStructuredData entry={entry} crumbs={breadcrumbs} isGuidePage />
      <Breadcrumbs
        containerClassNames={styles.breadcrumbsContainerMobile}
        prefix="guides"
        crumbs={breadcrumbs}
      />
      <div className={styles.guide}>
        <div className={styles.metaDataContainer}>
          <Breadcrumbs
            containerClassNames={styles.breadcrumbsContainerDesktop}
            prefix="guides"
            crumbs={breadcrumbs}
          />
          <div className={styles.metaData}>
            <h1 className={styles.title}>{entry.title}</h1>
            {pageAuthors && (
              <AuthorByLine
                authorEntry={pageAuthors}
                lastUpdated={entry.sys.publishedAt}
              />
            )}
          </div>
        </div>
        <div className={styles.guideContent}>
          <div className={styles.contentContainer}>
            <div className={styles.content}>
              {entry.blocks && (
                <BlockLoaderNonFields entry={entry} field="blocks" />
              )}
            </div>
            {isTableOfContentsShown && (
              <TableOfContents
                titles={titles}
                wrapperClasses={styles.tableOfContents}
              />
            )}
          </div>
        </div>
      </div>
      <ConversionBar />
    </Layout>
  );
};

interface GuidePageParams extends ParsedUrlQuery {
  locale: string;
  guide: string;
}

export const getStaticProps: GetStaticProps<
  Omit<PageData, 'contentType'>,
  GuidePageParams
> = async ({ params, preview = false }) => {
  const { locale, guide } = params || {};
  const contentfulLocale = getContentfulLocale(locale);
  const brand = await getBrand(process.env.BRAND_ID || null, locale);

  const gqEntries = await fetchContentfulGql({
    query: GET_GUIDE_PAGE,
    variables: {
      locale: contentfulLocale,
      preview: false,
      slug: guide,
    },
  });

  const entries = gqEntries.data.guidePageCollection.items;
  const entry = entries.filter((i: GuideItem) => !i.disableLocale)[0] || null;

  if (!entry) {
    return {
      notFound: true,
    };
  }

  const pageAuthors = getNormalizedAuthorGql({
    author: entry && entry.pageAuthor ? entry.pageAuthor : null,
    medicalReviewer:
      entry && entry.medicallyReviewed ? entry.medicallyReviewed : null,
    locale: locale || 'en',
  });

  return {
    props: {
      entry,
      locale,
      alternateSlugs: await getAlternatePages(
        entries[0] ? entries[0].sys.id : '',
        preview,
      ),
      preview,
      footer: getMainFooter(locale),
      pageAuthors,
      brand,
    },
    revalidate: config.defaults.secondsForPageRevalidation,
  };
};

export const getStaticPaths: GetStaticPaths<GuidePageParams> = async () => {
  if (skipServerSideBuild()) {
    return {
      paths: [],
      fallback: STATIC_PATH_FALLBACK,
    };
  }

  const paths: { params: { locale: string; guide: string } }[] = [];

  const getEntries = config.locales.map(async (locale: ConfigLocale) => {
    const entries = await contentfulClientPreview.getEntries<IGuidePageFields>({
      content_type: 'guidePage',
      locale: locale.contentful,
      limit: 1000,
      select: 'fields.slug,fields.disableLocale',
    });

    entries.items.forEach((item) => {
      if (!item.fields.disableLocale && item.fields.slug) {
        paths.push({
          params: {
            locale: locale.main,
            guide: item.fields.slug,
          },
        });
      }
    });
  });

  await Promise.all(getEntries);

  return {
    paths,
    fallback: 'blocking',
  };
};

export default Guide;
